/**
 * The element queue push algorithm (DESIGN 2.2, cancellation table 2.3).
 */

import type { BaseElement, Element } from './elements.js';
import { MAX_QUEUE, OPPOSITES } from './elements.js';

export type PushOutcome = 'added'|'combined'|'degraded'|'cancelled'|'full'|'self_shock';

/** `index` = slot touched (added, replaced or removed), -1 if none (full, self_shock). */
export interface PushResult { queue: Element[]; outcome: PushOutcome; index: number }

/**
 * Breakdowns (DESIGN 2.2 step 2, extended): pressing the key turns the most recent
 * fused element on the left back into the element on the right.
 * Fire thaws Ice and dries Mud; Cold condenses Steam and cools Lava; Water quenches Lava.
 */
const DEGRADES: Partial<Record<BaseElement, readonly [Element, Element][]>> = {
  fire: [['ice', 'water'], ['mud', 'earth']],
  cold: [['steam', 'water'], ['lava', 'earth']],
  water: [['lava', 'earth']],
};

/**
 * Fusions (DESIGN 2.2 step 3, extended). One rule for all of them: the key you press
 * fuses into the most recent matching element already queued, so ORDER MATTERS.
 *   Water then Fire = Steam    Water then Cold = Ice
 *   Water then Earth = Mud     Fire then Earth = Lava     Water then Life = Spring
 * The reverse orders stay separate, which keeps the classics reachable:
 * Earth then Fire is still a Fireball, Earth then Q R still a Hailstone boulder.
 */
const COMBINES: Partial<Record<BaseElement, readonly [Element, Element][]>> = {
  fire: [['water', 'steam']],
  cold: [['water', 'ice']],
  earth: [['water', 'mud'], ['fire', 'lava']],
  life: [['water', 'spring']],
};

/** Most recent slot holding any `from` of the pairs; returns [index, replacement] or null. */
function mostRecent(queue: readonly Element[], pairs: readonly [Element, Element][] | undefined): [number, Element] | null {
  if (!pairs) return null;
  for (let i = queue.length - 1; i >= 0; i--) {
    const hit = pairs.find(([from]) => from === queue[i]);
    if (hit) return [i, hit[1]];
  }
  return null;
}

function replaceAt(queue: readonly Element[], i: number, el: Element): Element[] {
  const next = [...queue];
  next[i] = el;
  return next;
}

/**
 * Push a pressed element onto a queue. Never mutates `queue`.
 * Evaluated in this order:
 * 1. lightning while casterWet -> self_shock, queue [].
 * 2. degrade: fire replaces last ice with water; cold replaces last steam with water.
 * 3. cancel: remove the most recently queued OPPOSITES[x] member, discard x.
 * 4. combine: fire replaces last water with steam; cold replaces last water with ice.
 * 5. full (MAX_QUEUE) -> unchanged.
 * 6. append.
 *
 * DESIGN 2.2 writes combine before cancel, but that lets `R Q F` build
 * [cold, steam] and `F Q R` build [fire, ice]: opposite pairs living in one
 * queue, which 2.2's own consequences and test 20 rule out, and which would
 * let a single caster wet-and-chill (freeze) in one spray. Cancelling first
 * keeps "opposites always cancel" true everywhere and keeps the freeze set-up
 * a two-cast (or two-player) play. Degrade still comes first, so Fire on
 * [ice, water] thaws the Ice instead of steaming the Water.
 */
export function pushElement(queue: readonly Element[], x: BaseElement, casterWet: boolean): PushResult {
  // DESIGN 2.2 step 1: the server adds 250 damage and the shock_self event.
  if (x === 'lightning' && casterWet) return { queue: [], outcome: 'self_shock', index: -1 };

  // DESIGN 2.2 step 2.
  const degraded = mostRecent(queue, DEGRADES[x]);
  if (degraded) return { queue: replaceAt(queue, degraded[0], degraded[1]), outcome: 'degraded', index: degraded[0] };

  // DESIGN 2.2 step 4, hoisted above combine (see the doc comment).
  let cancelAt = -1;
  for (let i = queue.length - 1; i >= 0; i--) {
    const y = queue[i];
    if (y !== undefined && OPPOSITES[x].includes(y)) { cancelAt = i; break; }
  }
  if (cancelAt >= 0) {
    const next = [...queue];
    next.splice(cancelAt, 1);
    return { queue: next, outcome: 'cancelled', index: cancelAt };
  }

  // DESIGN 2.2 step 3.
  const combined = mostRecent(queue, COMBINES[x]);
  if (combined) return { queue: replaceAt(queue, combined[0], combined[1]), outcome: 'combined', index: combined[0] };

  // DESIGN 2.2 step 5.
  if (queue.length >= MAX_QUEUE) return { queue: [...queue], outcome: 'full', index: -1 };

  // DESIGN 2.2 step 6.
  return { queue: [...queue, x], outcome: 'added', index: queue.length };
}

/** The empty queue (Backspace / Escape, DESIGN 6.1). */
export function clearQueue(): Element[] {
  return [];
}
